/** @typedef {'hair' | 'skin' | 'eye' | 'eyeShine' | 'shirt' | 'shirtHighlight' | 'pants' | 'shoes' | 'outline' | 'headwear' | 'headwearAccent' | 'empty'} PixelPersonZone */

import { PIXEL_PERSON_HEADWEAR_GRIDS, mergeHeadwearOntoGrid } from './pixelPersonHeadwear';
import { MASTER_AGENT_HEADWEAR_ID, SYSTEM_AGENT_HEADWEAR_ID } from './pixelPersonPalettes';

const _ = 'empty';
const o = 'outline';
const h = 'hair';
const s = 'skin';

export const PIXEL_PERSON_HAIR_STYLE_IDS = [
  'bob',
  'short',
  'long',
  'bun',
  'spiky',
  'parted',
  'curly',
  'buzz',
  'pigtails',
];

/** Head/body grid without hair — hair layers are merged on top. */
export const PIXEL_PERSON_BODY_GRID = [
  [_, _, _, _, _, o, o, o, o, _, _, _, _, _],
  [_, _, _, o, o, _, _, _, _, o, o, _, _, _],
  [_, _, o, _, _, _, _, _, _, _, _, o, _, _],
  [_, _, o, _, _, _, _, _, _, _, _, o, _, _],
  [_, o, _, 'skin', _, _, _, _, _, _, _, _, o, _],
  [_, o, 'skin', 'skin', 'skin', 'skin', 'skin', 'skin', 'skin', 'skin', 'skin', 'skin', o, _],
  [_, o, 'skin', 'skin', 'skin', 'skin', 'skin', 'skin', 'skin', 'skin', 'skin', 'skin', o, _],
  [_, _, o, 'skin', 'skin', 'eye', 'skin', 'skin', 'eye', 'skin', 'skin', o, _, _],
  [_, _, o, 'skin', 'skin', o, 'skin', 'skin', o, 'skin', 'skin', o, _, _],
  [_, _, o, 'shirt', o, 'skin', 'skin', 'skin', 'skin', o, 'shirt', o, _, _],
  [_, o, 'skin', 'shirt', 'shirt', o, o, o, o, 'shirt', 'shirt', 'skin', o, _],
  [o, 'skin', 'skin', o, 'shirt', 'shirt', 'shirt', 'shirt', 'shirt', 'shirt', o, 'skin', 'skin', o],
  [o, 'skin', 'skin', o, 'shirt', 'shirt', 'shirt', 'shirt', 'shirt', 'shirt', o, 'skin', 'skin', o],
  [_, o, o, 'pants', o, o, o, o, o, o, 'pants', o, o, _],
  [_, _, o, 'pants', 'pants', 'pants', 'pants', 'pants', 'pants', 'pants', 'pants', o, _, _],
  [_, _, o, 'pants', 'pants', 'pants', o, o, 'pants', 'pants', 'pants', o, _, _],
  [_, _, o, 'shoes', 'shoes', 'shoes', o, o, 'shoes', 'shoes', 'shoes', o, _, _],
  [_, _, _, o, o, o, _, _, o, o, o, _, _, _],
];

/** Hair-only overlays (same 14×18). `hair` and `outline` cells merge over the body grid. */
export const PIXEL_PERSON_HAIR_STYLE_GRIDS = {
  bob: [
    [_, _, _, _, _, o, o, o, o, _, _, _, _, _],
    [_, _, _, o, o, h, h, h, h, o, o, _, _, _],
    [_, _, o, h, h, h, h, h, h, h, h, o, _, _],
    [_, _, o, h, h, h, h, h, h, h, h, o, _, _],
    [_, o, h, s, h, h, h, h, h, h, h, h, o, _],
    [_, o, s, s, s, s, h, h, h, h, h, s, o, _],
  ],
  short: [
    [_, _, _, _, _, o, o, o, o, _, _, _, _, _],
    [_, _, _, o, o, h, h, h, h, o, o, _, _, _],
    [_, _, o, h, h, h, h, h, h, h, h, o, _, _],
    [_, _, o, h, h, s, s, s, s, h, h, o, _, _],
    [_, o, h, s, s, h, h, h, h, s, s, h, o, _],
    [_, o, s, s, s, s, h, h, s, s, s, s, o, _],
  ],
  long: [
    [_, _, _, _, _, o, o, o, o, _, _, _, _, _],
    [_, _, _, o, o, h, h, h, h, o, o, _, _, _],
    [_, _, o, h, h, h, h, h, h, h, h, o, _, _],
    [_, _, o, h, h, h, h, h, h, h, h, o, _, _],
    [_, o, h, s, h, h, h, h, h, h, h, h, o, _],
    [_, o, h, s, s, s, h, h, h, s, s, h, o, _],
    [_, o, h, s, s, s, s, s, s, s, s, h, o, _],
  ],
  bun: [
    [_, _, _, _, o, h, h, h, h, o, _, _, _, _],
    [_, _, _, o, o, h, h, h, h, o, o, _, _, _],
    [_, _, o, h, h, s, s, s, s, h, h, o, _, _],
    [_, _, o, h, h, s, s, s, s, h, h, o, _, _],
    [_, o, h, s, s, h, h, h, h, s, s, h, o, _],
    [_, o, s, s, s, s, h, h, s, s, s, s, o, _],
  ],
  spiky: [
    [_, _, _, h, o, h, h, h, o, h, _, _, _, _],
    [_, _, _, o, o, h, h, h, h, o, o, _, _, _],
    [_, _, o, h, h, h, h, h, h, h, h, o, _, _],
    [_, _, o, h, h, s, s, s, s, h, h, o, _, _],
    [_, o, h, s, s, h, h, h, h, s, s, h, o, _],
    [_, o, s, s, s, s, h, h, s, s, s, s, o, _],
  ],
  parted: [
    [_, _, _, _, _, o, o, o, o, _, _, _, _, _],
    [_, _, _, o, o, h, h, h, h, o, o, _, _, _],
    [_, _, o, h, h, h, s, s, h, h, h, o, _, _],
    [_, _, o, h, h, s, s, s, s, h, h, o, _, _],
    [_, o, h, s, s, h, h, h, h, s, s, h, o, _],
    [_, o, h, s, s, s, h, h, s, s, s, h, o, _],
  ],
  curly: [
    [_, _, o, h, h, h, h, h, h, h, h, o, _, _],
    [o, h, h, h, h, h, h, h, h, h, h, h, o, _],
    [o, h, h, h, h, h, h, h, h, h, h, h, o, _],
    [_, o, h, h, h, h, h, h, h, h, h, o, _, _],
    [_, o, h, h, s, h, h, h, h, s, h, h, o, _],
    [_, o, h, s, s, s, h, h, s, s, s, h, o, _],
  ],
  buzz: [
    [_, _, _, _, _, o, o, o, o, _, _, _, _, _],
    [_, _, _, o, o, h, h, h, h, o, o, _, _, _],
    [_, _, o, h, h, s, s, s, s, h, h, o, _, _],
    [_, _, o, s, s, s, s, s, s, s, s, o, _, _],
    [_, o, h, s, s, s, s, s, s, s, s, h, o, _],
  ],
  pigtails: [
    [_, _, _, _, _, o, o, o, o, _, _, _, _, _],
    [_, _, _, o, o, h, h, h, h, o, o, _, _, _],
    [_, _, o, h, h, h, h, h, h, h, h, o, _, _],
    [h, h, o, h, h, h, h, h, h, h, h, o, h, h],
    [h, o, h, s, h, h, h, h, h, h, s, h, o, h],
    [_, o, s, s, s, s, h, h, h, s, s, s, o, _],
  ],
};

/**
 * @param {readonly (readonly PixelPersonZone[])[]} body
 * @param {readonly (readonly PixelPersonZone[])[]} hairOverlay
 * @returns {PixelPersonZone[][]}
 */
export function mergePixelPersonGrid(body, hairOverlay) {
  return body.map((row, rowIdx) =>
    row.map((zone, colIdx) => {
      const hairZone = hairOverlay[rowIdx]?.[colIdx];
      if (hairZone === 'hair' || hairZone === 'skin' || hairZone === 'outline') {
        return hairZone;
      }
      return zone;
    })
  );
}

/**
 * @param {string} styleId
 * @param {string} [headwearId]
 * @returns {PixelPersonZone[][]}
 */
export function buildPixelPersonGrid(styleId, headwearId = 'none') {
  const hairOverlay = PIXEL_PERSON_HAIR_STYLE_GRIDS[styleId] ?? PIXEL_PERSON_HAIR_STYLE_GRIDS.bob;
  const withHair = mergePixelPersonGrid(PIXEL_PERSON_BODY_GRID, hairOverlay);

  if (!headwearId || headwearId === 'none') {
    return withHair;
  }

  const headwearGridKey =
    headwearId === SYSTEM_AGENT_HEADWEAR_ID || headwearId === MASTER_AGENT_HEADWEAR_ID
      ? 'hardhat'
      : headwearId;
  const headwearOverlay = PIXEL_PERSON_HEADWEAR_GRIDS[headwearGridKey];
  if (!headwearOverlay) {
    return withHair;
  }

  return mergeHeadwearOntoGrid(withHair, headwearOverlay);
}
